// Local type contracts for creator-collections-listing — ported from
// eb-ui/packages/attendee/creator-collections-listing/src/types/index.ts.
//
// The eb-ui app is custom-SSR: the Django backend pre-fetches ALL primary page
// data and injects it via `window.__SERVER_DATA__` (AppProps = CoreApplicationContext
// & CoreGoogleAnalyticsSettings & CollectionProps & FeatureFlagsProps). We reconstruct
// the same shape in getServerSideProps. Two render paths: a full collection, or a
// "collection_moved" error state.
//
// event-renderer's FormattedEvent / DestinationEvent are sourced from the sanctioned
// @eventbrite/consumer-search.event-renderer where used; the origin (Core snake_case)
// event shape below is what __SERVER_DATA__ / the BFF return before transform.

import type { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';

/** Core snake_case origin event shape (what the backend + BFF return). */
export interface EventOriginType {
    id: string;
    logo?: { url?: string };
    listed: boolean;
    is_free: boolean;
    online_event: boolean;
    start: {
        utc: string;
        timezone: string;
        local: string;
        date_header?: string;
        formatted_time?: string;
    };
    name: { text: null | string };
    saves?: { saved_by_you: boolean };
    hide_start_date: boolean;
    url: string;
    event_sales_status?: {
        default_message: null | string;
        sales_status: string;
        message: null | string;
        message_code: null | string;
        message_type: null | string;
    };
    series_id?: string | null;
    image?: {
        edge_color_set: boolean;
        edge_color: string | null;
        url: string;
        original: { url: string; width: number; height: number };
        crop_mask: { width: number; height: number; top_left: { x: number; y: number } };
        aspect_ratio: string;
        id: string;
    };
    is_protected_event?: boolean;
    price_range?: string;
    ticket_availability?: {
        is_sold_out: boolean;
        is_free: boolean;
        minimum_ticket_price: {
            currency: string;
            major_value: string;
            value: number;
            display: string;
        };
        has_available_tickets: boolean;
        maximum_ticket_price: {
            currency: string;
            major_value: string;
            value: number;
            display: string;
        };
    };
    subtitle?: string;
    tickets_by?: string;
    organizer_id: string;
    primary_organizer?: { name: string; num_followers: number };
    is_series_parent: boolean;
    num_children?: number;
    series?: {
        url: string;
        counts: { current_future: number };
        next_dates: { start: string }[];
    };
    venue?: {
        age_restriction: string;
        user_id: string;
        name: string;
        longitude: string;
        address: {
            city: string;
            country: string;
            region: string;
            longitude: string;
            localized_address_display: string;
            postal_code: string;
            address_1: string;
            latitude: string;
            localized_multi_line_address_display: string[];
            localized_area_display: string;
        };
        latitude: string;
        capacity: number;
        organizer_id: string;
        google_place_id: string;
        id: string;
    };
    [key: string]: unknown;
}

/** A card-ready event after transform (the vertical event card model). */
export interface CollectionEventCard {
    id: string;
    imageUrl: string;
    isFree: boolean;
    isPrivate?: boolean;
    isRestricted?: boolean;
    isOnlineEvent: boolean;
    locationInfo: string;
    name: string;
    isRepeatingEvent?: boolean;
    savedByYou: boolean;
    startDate?: string;
    startTime: string;
    timezone: string;
    url: string;
    eventSalesStatus?: {
        salesStatus: string;
        message: string;
        messageType: string;
        messageCode: string;
        defaultMessage?: string;
    };
    isSoldOut?: boolean;
    edgeColor?: string;
    formattedPriceString?: string;
    minPrice?: { currency: string; currencyFormat?: string; minPriceValue: number };
    repeatingInstanceCount?: number;
    subtitle?: string;
    ticketsBy?: string;
    organizerId?: string;
    organizerName?: string;
    organizerFollowerCount?: number;
    [key: string]: unknown;
}

export interface CollectionImage {
    edge_color?: string | null;
    url: string;
    /** Full-resolution original (no resize params) — the hero's sharp large candidate. */
    originalUrl?: string;
    srcSet?: string;
    crop_mask: null | {
        width: number;
        height: number;
        top_left: { x: number; y: number };
    };
}

export interface FollowStatus {
    followed_by_you: boolean;
    followed_at?: unknown;
    num_followers: number;
}

export interface Organizer {
    id: string;
    name: string;
    url: string;
    logo?: null | { url: string };
    description: string;
    follow_status: FollowStatus;
    organization_id: string;
    /** Organizers-read extras (SEO Organization JSON-LD) — absent on the collections expand. */
    twitter?: string | null;
    facebook?: string | null;
    website?: string | null;
}

export interface Collection {
    id: string;
    name: string;
    slug: string;
    image: CollectionImage | null;
    organizer: Organizer;
    summary?: string;
    status: string;
    url: string;
    is_autocreated?: boolean;
    is_private?: boolean;
    organization_id: string;
}

export interface Pagination {
    object_count: number;
    continuation?: string;
    page_count: number;
    page_size: number;
    has_more_items: boolean;
    page_number: number;
}

export interface EventListProps {
    pagination: Pagination;
    events: EventOriginType[];
}

export interface CollectionEventsProps {
    past: EventListProps;
    upcoming: EventListProps;
}

export interface MoreCollectionsFromThisOrganizer {
    pagination: Pagination;
    event_groups: CollectionCardOriginType[];
}

export interface OtherEventsYouMayLike {
    pagination: Pagination;
    events: DestinationEvent[];
}

export enum CollectionStatus {
    Archived = 'archived',
    Deleted = 'deleted',
    Draft = 'draft',
    Live = 'live',
}

export interface CollectionCardOriginType {
    status: string;
    name: string;
    summary: string;
    organization_id: string;
    image_id: string | null;
    image?: { url: string };
    id: string;
    organizer_id: string;
    relative_url: string;
    absolute_url?: string;
    type: string;
    slug?: string;
    event_count: {
        past_published?: number;
        current_future_published: number;
        current_public_future_published?: number;
        current_private_future_published?: number;
        draft?: number;
    };
    organizer?: Record<string, unknown>;
    [key: string]: unknown;
}

export enum AffiliateCodes {
    EventsInOrganizerCollection = 'odcleoeventsincollection',
    SocialShareToolsInCollection = 'odclsxcollection',
    RelatedCollectionsFromOrganizer = 'odclrlmcfto',
    RelatedEventsFromOrganizer = 'odclrlmefto',
    RelatedEventsOtherOrganizers = 'ebdsreoeymlcollection',
}

/** social-share ShareProps shape (shared module's contract). */
export interface ShareProps {
    facebookOptions?: Record<string, unknown>;
    serverUrl?: string;
    twitterHandle?: string;
    isMobile?: boolean;
    trackingCategory?: string;
    utmOptions?: Record<string, string>;
    emailOptions?: Record<string, unknown>;
    [key: string]: unknown;
}

interface Env {
    localeInfo: Record<string, unknown> & { locale?: string; uses_language_subdirectory?: boolean; default_currency?: string };
    ebDomain?: string;
    serverUrl?: string;
    defaultCurrency?: string;
    [key: string]: unknown;
}
interface Request {
    correlation_id?: string;
    session_id?: string;
    path?: string;
    is_bot?: boolean;
    lifecycle?: string;
    params?: Record<string, unknown>;
    [key: string]: unknown;
}
interface User {
    isAuthenticated?: boolean;
    email?: string | null;
    guestId?: string;
    publicId?: string | null;
    fullName?: string | null;
    activeOrganization?: Record<string, unknown> | null;
    [key: string]: unknown;
}
interface GSettings {
    appName?: string;
    appVersion?: string;
    isActive?: boolean;
    guestPartnerId?: string;
    userPartnerId?: string | null;
    [key: string]: unknown;
}

/** The full reconstructed __SERVER_DATA__ for a live collection. */
export interface CollectionServerData {
    env: Env;
    request: Request;
    user: User;
    footerLinks: Record<string, unknown>;
    gaSettings: GSettings;
    app_name?: string;
    app_version?: string;
    collection: Collection;
    events_in_collection: CollectionEventsProps;
    share_props: ShareProps;
    permissions: { can_manage: boolean };
    more_collections_from_this_organizer?: MoreCollectionsFromThisOrganizer;
    other_events_you_may_like?: OtherEventsYouMayLike;
    upcoming_events_count?: number;
    pasts_events_count?: number;
    preview?: boolean;
    featureFlags?: Record<string, boolean>;
}

/** The "collection moved / doesn't exist" state. */
export interface CollectionMovedServerData {
    env: Env;
    request: Request;
    user: User;
    footerLinks: Record<string, unknown>;
    gaSettings: GSettings;
    app_name?: string;
    app_version?: string;
    collection_moved: boolean;
    other_events_you_may_like?: OtherEventsYouMayLike;
    share_props: ShareProps;
    preview?: boolean;
    featureFlags?: Record<string, boolean>;
}

export type ServerData = CollectionServerData | CollectionMovedServerData;
